import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Database, RefreshCw, Trash2, Search, Filter, AlertTriangle,
  Activity, HardDrive, Clock, Users, Hash, List, Package,
  Zap, Info, ChevronDown, ChevronRight, Loader2, CheckCircle,
  XCircle, AlertCircle, TrendingUp, TrendingDown, Minus,
  Key, Timer, Server, Cpu
} from 'lucide-react';
import toast from 'react-hot-toast';
import cacheStorageService, { 
  RedisKeyValueDto, 
  RedisStatsDto,
  CollectionTimestamps 
} from '@/services/cache-storage.service';

// Stats Card Component
const StatsCard = ({ 
  title, 
  value, 
  subValue, 
  icon: Icon, 
  trend,
  color = 'blue' 
}: {
  title: string;
  value: string | number;
  subValue?: string;
  icon: React.ElementType;
  trend?: 'up' | 'down' | 'stable';
  color?: string;
}) => {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500';
  
  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-${color}-50`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        {trend && <TrendIcon className={`w-4 h-4 ${trendColor}`} />}
      </div>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
      </div>
    </div>
  );
};

// Key Item Component
const KeyItem = ({ 
  keyData, 
  onDelete 
}: { 
  keyData: RedisKeyValueDto; 
  onDelete: (key: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const typeColors = cacheStorageService.getTypeColor(keyData.type);
  
  const renderValue = () => {
    if (!expanded) return null;
    
    const value = keyData.value;
    if (value === null || value === undefined) {
      return <span className="text-gray-400">null</span>;
    }
    
    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span className="text-sm text-gray-700">{String(value)}</span>;
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              {expanded ? 
                <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                <ChevronRight className="w-4 h-4 text-gray-500" />
              }
            </button>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors}`}>
              {keyData.type}
            </span>
            <code className="text-sm font-mono text-gray-800 break-all">
              {keyData.key}
            </code>
          </div>
          
          <div className="flex items-center gap-4 mt-2 ml-7 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Timer className="w-3 h-3" />
              TTL: {cacheStorageService.formatTTL(keyData.ttl)}
            </span>
            {keyData.size && (
              <span className="flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {cacheStorageService.formatBytes(keyData.size)}
              </span>
            )}
          </div>
          
          {expanded && (
            <div className="mt-3 ml-7">
              {renderValue()}
            </div>
          )}
        </div>
        
        <button
          onClick={() => onDelete(keyData.key)}
          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Delete this key"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default function RedisCacheMonitor() {
  const queryClient = useQueryClient();
  const [searchPattern, setSearchPattern] = useState('*');
  const [inputPattern, setInputPattern] = useState('*');
  const [cursor, setCursor] = useState('0');
  const [allKeys, setAllKeys] = useState<RedisKeyValueDto[]>([]);
  const [groupByPrefix, setGroupByPrefix] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force refetch
  
  // Fetch Redis stats
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['redis-stats'],
    queryFn: () => cacheStorageService.getStats(),
    refetchInterval: 30000 // Refresh every 30 seconds
  });
  
  // Fetch collection timestamps
  const { data: timestamps } = useQuery({
    queryKey: ['collection-timestamps'],
    queryFn: () => cacheStorageService.getCollectionTimestamps()
  });
  
  // Fetch Redis keys
  const { 
    data: keysData, 
    isLoading: isLoadingKeys, 
    refetch: refetchKeys 
  } = useQuery({
    queryKey: ['redis-keys', searchPattern, cursor, refreshKey],
    queryFn: () => cacheStorageService.getRedisKeys(searchPattern, 100, cursor),
    enabled: true
  });
  
  // Clear cache mutation
  const clearCacheMutation = useMutation({
    mutationFn: ({ pattern, reason }: { pattern: string; reason: string }) =>
      cacheStorageService.clearCache(pattern, reason),
    onSuccess: (data) => {
      toast.success(`Cleared ${data.deletedCount} keys matching pattern "${data.pattern}"`);
      queryClient.invalidateQueries({ queryKey: ['redis-keys'] });
      queryClient.invalidateQueries({ queryKey: ['redis-stats'] });
      setAllKeys([]);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to clear cache');
    }
  });
  
  // Delete single key mutation
  const deleteKeyMutation = useMutation({
    mutationFn: (key: string) => cacheStorageService.clearCache(key, 'Manual deletion from monitor'),
    onSuccess: (data, key) => {
      toast.success(`Deleted key: ${key}`);
      setAllKeys(prev => prev.filter(k => k.key !== key));
      refetchKeys();
      refetchStats();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete key');
    }
  });
  
  // Load more keys
  useEffect(() => {
    if (keysData) {
      if (cursor === '0') {
        setAllKeys(keysData.keys);
      } else {
        setAllKeys(prev => [...prev, ...keysData.keys]);
      }
    }
  }, [keysData, cursor]);
  
  // Handle search
  const handleSearch = () => {
    setCursor('0');
    setAllKeys([]);
    setSearchPattern(inputPattern || '*');
    setRefreshKey(prev => prev + 1); // Force refetch
  };
  
  // Handle load more
  const handleLoadMore = () => {
    if (keysData?.hasMore && keysData.cursor !== '0') {
      setCursor(keysData.cursor);
    }
  };
  
  // Handle clear cache
  const handleClearCache = () => {
    const pattern = prompt('Enter pattern to clear (e.g., TEMP_*, USER_*, or * for all):');
    if (!pattern) return;
    
    const reason = prompt('Enter reason for clearing cache:');
    if (!reason) return;
    
    const confirmMsg = pattern === '*' 
      ? 'This will clear ALL cache! Type "CONFIRM" to proceed:'
      : `This will clear all keys matching "${pattern}". Type "CONFIRM" to proceed:`;
    
    const confirmation = prompt(confirmMsg);
    if (confirmation !== 'CONFIRM') {
      toast.error('Clear operation cancelled');
      return;
    }
    
    clearCacheMutation.mutate({ pattern, reason });
  };
  
  // Toggle group
  const toggleGroup = (prefix: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(prefix)) {
      newExpanded.delete(prefix);
    } else {
      newExpanded.add(prefix);
    }
    setExpandedGroups(newExpanded);
  };
  
  // Group keys by prefix if enabled
  const displayKeys = () => {
    if (!groupByPrefix) {
      return allKeys;
    }
    
    const grouped = cacheStorageService.groupKeysByPrefix(allKeys);
    return grouped;
  };
  
  // Calculate cache hit ratio
  const hitRatio = stats ? cacheStorageService.calculateHitRatio(
    stats.keyspaceHits,
    stats.keyspaceMisses
  ) : 0;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="w-8 h-8 text-red-600" />
          Redis Cache Monitor
        </h1>
        <p className="text-gray-600 mt-2">
          Monitor and manage Redis cache storage in real-time
        </p>
      </div>
      
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Total Keys"
            value={stats.keyCount.toLocaleString()}
            icon={Key}
            color="blue"
          />
          <StatsCard
            title="Memory Usage"
            value={stats.memoryUsage}
            subValue={`Peak: ${stats.usedMemoryPeak}`}
            icon={Cpu}
            color="green"
            trend={stats.evictedKeys > 0 ? 'down' : 'stable'}
          />
          <StatsCard
            title="Cache Hit Ratio"
            value={`${hitRatio.toFixed(1)}%`}
            subValue={`${stats.keyspaceHits.toLocaleString()} hits`}
            icon={Activity}
            color="purple"
            trend={hitRatio > 80 ? 'up' : 'down'}
          />
          <StatsCard
            title="Uptime"
            value={cacheStorageService.formatUptime(stats.uptimeSeconds)}
            subValue={`v${stats.version}`}
            icon={Clock}
            color="orange"
          />
        </div>
      )}
      
      {/* Server Info */}
      {stats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Server className="w-5 h-5" />
            Server Information
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Connected Clients</p>
              <p className="font-semibold">{stats.connectedClients}</p>
            </div>
            <div>
              <p className="text-gray-600">Commands Processed</p>
              <p className="font-semibold">{stats.totalCommandsProcessed.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-600">Evicted Keys</p>
              <p className={`font-semibold ${stats.evictedKeys > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {stats.evictedKeys}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Cache Misses</p>
              <p className="font-semibold">{stats.keyspaceMisses.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Collection Timestamps */}
      {timestamps && Object.keys(timestamps).length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Collection Last Updated
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(timestamps).map(([collection, timestamp]) => (
              <div key={collection} className="text-sm">
                <p className="text-gray-600 capitalize">{collection}</p>
                <p className="font-mono text-xs">
                  {new Date(timestamp).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Keys Management */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Hash className="w-5 h-5" />
              Cache Keys
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setGroupByPrefix(!groupByPrefix)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  groupByPrefix 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Package className="w-4 h-4 inline mr-1" />
                Group by Prefix
              </button>
              <button
                onClick={() => {
                  setCursor('0');
                  setAllKeys([]);
                  setRefreshKey(prev => prev + 1); // Force refetch
                  refetchStats(); // Also refresh stats
                }}
                disabled={isLoadingKeys}
                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh keys"
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingKeys ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleClearCache}
                disabled={clearCacheMutation.isLoading}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {clearCacheMutation.isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Clear Cache
              </button>
            </div>
          </div>
          
          {/* Search */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={inputPattern}
                onChange={(e) => setInputPattern(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search pattern (e.g., USER_*, CACHE_*, or * for all)"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoadingKeys}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isLoadingKeys ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </button>
          </div>
          
          {/* Results info */}
          <div className="mt-3 text-sm text-gray-600">
            {keysData && (
              <p>
                Showing {allKeys.length} keys
                {keysData.hasMore && ' (more available)'}
                {searchPattern !== '*' && ` matching pattern "${searchPattern}"`}
              </p>
            )}
          </div>
        </div>
        
        {/* Keys List */}
        <div className="p-4">
          {isLoadingKeys && allKeys.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : allKeys.length === 0 ? (
            <div className="text-center py-8">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No keys found</p>
              {searchPattern !== '*' && (
                <p className="text-sm text-gray-400 mt-1">
                  Try a different search pattern
                </p>
              )}
            </div>
          ) : (
            <>
              {!groupByPrefix ? (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {allKeys.map((key) => (
                    <KeyItem
                      key={key.key}
                      keyData={key}
                      onDelete={(k) => deleteKeyMutation.mutate(k)}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {Object.entries(displayKeys() as Record<string, RedisKeyValueDto[]>).map(([prefix, keys]) => (
                    <div key={prefix} className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleGroup(prefix)}
                        className="w-full px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          {expandedGroups.has(prefix) ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="font-medium text-gray-900">{prefix}</span>
                          <span className="text-sm text-gray-500">({keys.length} keys)</span>
                        </div>
                      </button>
                      {expandedGroups.has(prefix) && (
                        <div className="p-2 space-y-2 border-t border-gray-200">
                          {keys.map((key) => (
                            <KeyItem
                              key={key.key}
                              keyData={key}
                              onDelete={(k) => deleteKeyMutation.mutate(k)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Load More */}
              {keysData?.hasMore && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingKeys}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {isLoadingKeys ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Health Status */}
      {stats && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Health Status
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
              <span className="text-sm text-gray-600">Cache Hit Ratio</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{hitRatio.toFixed(1)}%</span>
                {hitRatio >= 80 ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : hitRatio >= 60 ? (
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
              <span className="text-sm text-gray-600">Memory Pressure</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {stats.evictedKeys > 0 ? 'High' : 'Normal'}
                </span>
                {stats.evictedKeys === 0 ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
              <span className="text-sm text-gray-600">Server Status</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Healthy</span>
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}